// Auto-bundled translations for the PassportOffer component.
// Source: worldle-cdn/src/translation (only English ships a `passportOffer` set;
// other locales fall back to English).
//
// Prices: the component reads the real prices from the auth server
// (GET /stripe/passport/prices) and fills them into the *Template strings.
// The fixed prices (saveBadge, annualMonthlyPrice, annualBillingDetail,
// monthlyPrice) are only shown if the prices can't be loaded.

export type PassportOfferTranslation = {
  eyebrow: string;
  title: string;
  story: string;
  benefitsTitle: string;
  benefitAdsTitle: string;
  benefitAdsDetail: string;
  gameListLabel: string;
  benefitSyncTitle: string;
  benefitSyncDetail: string;
  benefitArchiveTitle: string;
  benefitArchiveDetail: string;
  benefitEarlyAccessTitle: string;
  benefitEarlyAccessDetail: string;
  pricingTitle: string;
  saveBadge: string;
  /** {percent}: how much cheaper yearly is than 12 monthly payments, e.g. 37 */
  saveBadgeTemplate: string;
  annualLabel: string;
  annualMonthlyPrice: string;
  annualMonthlyPeriod: string;
  annualBillingDetail: string;
  /** {price}: the yearly price, e.g. $29.99 */
  annualBillingDetailTemplate: string;
  monthlyLabel: string;
  monthlyPrice: string;
  perMonth: string;
  monthlyBilling: string;
  cta: string;
  checkoutActiveSubscription: string;
  checkoutLogin: string;
  checkoutManage: string;
  checkoutError: string;
  supportingStudio: string;
  supportingClimate: string;
  finePrintBefore: string;
};

export const passportOfferTranslations = {
  en: {
    eyebrow: 'Teuteuf Passport',
    title: 'Every Teuteuf game. No ads. One subscription.',
    story:
      "Worldle started as a side project in January 2022 and became a global morning routine. Four years on, we're still a small independent team making games for millions of you every day",
    benefitsTitle: "What's included",
    benefitAdsTitle: 'No ads',
    benefitAdsDetail: 'across all 20 games',
    gameListLabel: 'Show all 20 games',
    benefitSyncTitle: 'Sync everywhere',
    benefitSyncDetail: 'your streaks, stats and progress, backed up on every device',
    benefitArchiveTitle: 'Play the archive',
    benefitArchiveDetail: 'replay past challenges in supported games',
    benefitEarlyAccessTitle: 'Early access',
    benefitEarlyAccessDetail: 'be first to new games and features',
    pricingTitle: 'Choose your plan',
    saveBadge: '-37%',
    saveBadgeTemplate: '-{percent}%',
    annualLabel: 'Yearly',
    annualMonthlyPrice: '$2.50',
    annualMonthlyPeriod: '/ month',
    annualBillingDetail: '$29.99 billed yearly',
    annualBillingDetailTemplate: '{price} billed yearly',
    monthlyLabel: 'Monthly',
    monthlyPrice: '$3.99',
    perMonth: '/ month',
    monthlyBilling: 'Billed monthly (cancel anytime)',
    cta: 'Get Teuteuf Passport',
    checkoutActiveSubscription: 'You already have an active subscription or lifetime premium.',
    checkoutLogin: 'Try logging in here',
    checkoutManage: 'Manage your subscription at',
    checkoutError: 'Something went wrong. Please try again later.',
    supportingStudio: "You'll be supporting an independent studio",
    supportingClimate: 'For each subscription we donate to carbon removal',
    finePrintBefore: 'Payments are managed securely through'
  }
} as const satisfies Record<string, PassportOfferTranslation>;

export type PassportOfferLanguage = keyof typeof passportOfferTranslations;

const fallbackLanguage: PassportOfferLanguage = 'en';

export function getPassportOfferTranslations(lang: string | undefined): PassportOfferTranslation {
  if (lang && lang in passportOfferTranslations) {
    return passportOfferTranslations[lang as PassportOfferLanguage];
  }
  return passportOfferTranslations[fallbackLanguage];
}
